/**
 * Trade Plan Generator — context builder + LLM dispatcher.
 *
 * Composes a structured "plan context" from the trader's Coach Profile +
 * recent regime + today's news events, then asks the LLM narrator to
 * write a forward-looking 3-5 sentence pre-market plan.
 *
 * Different from the synthesis narrator (which is retrospective —
 * "what happened, what to do") and the weekly digest narrator
 * (which is week-recap). Plan narration is FORWARD ("today, focus on X,
 * skip Y, watch out for Z at 8:30").
 *
 * Pure builder + a thin async dispatch wrapper around the existing
 * llmNarrator infrastructure (so prompt caching, voice personality,
 * spend telemetry all carry over).
 *
 * Returns the same { ok, text, fromCache, ... } shape narrate() returns.
 *
 * Design note: rather than introduce a new narrator with its own settings,
 * we reuse useLlmNarrator's narrate() but pass a custom `scope: 'plan'`
 * value the prompt builder treats specially. That keeps the LLM cost
 * meter, voice picker, and key handling all in one place.
 */

import { narrate as narrateService } from '../llm/llmNarrator.js';

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Build a structured plan context the LLM can reason over. Pure — no I/O.
 *
 * @param {Object} args
 * @param {Object} args.profile         current CoachProfile
 * @param {Array}  args.recentTrades    closed trades from the last N days
 * @param {Array}  args.todayEvents     economic events for today (high/medium impact)
 * @param {string} args.dateStr         YYYY-MM-DD trading day the plan is for
 * @param {Object} [args.priorAdherence]  optional: { onPlanCount, offPlanCount, headline } from yesterday's analyzer
 * @param {Function} args.getTradeNetPnL
 * @param {Function} [args.getAccountById]
 * @returns {{ topEdges, topLeaks, recent, todayEvents, dateStr, priorAdherence }}
 */
export const buildPlanContext = ({
  profile = null,
  recentTrades = [],
  todayEvents = [],
  dateStr = '',
  priorAdherence = null,
  getTradeNetPnL,
  getAccountById,
} = {}) => {
  // Top edges + leaks from profile (already priority-sorted by buildProfile)
  const topEdges = (profile?.edges || []).slice(0, 3).map(e => ({
    title: e.title || '',
    detail: e.detail || '',
    action: e.action || '',
    scope: e.scope || '',
    scopeId: e.scopeId || '',
    occurrences: e.occurrences || 1,
  }));
  const topLeaks = (profile?.leaks || []).slice(0, 3).map(l => ({
    title: l.title || '',
    detail: l.detail || '',
    action: l.action || '',
    scope: l.scope || '',
    scopeId: l.scopeId || '',
    occurrences: l.occurrences || 1,
  }));

  // Recent regime — last 7 days perf rolled up. Cheap aggregate; the LLM
  // just needs the headline number, not full equity-curve detail.
  let recent = null;
  if (Array.isArray(recentTrades) && recentTrades.length > 0) {
    const netPnlFor = (t) => {
      const acc = getAccountById ? getAccountById(t.accountId) : null;
      const r = getTradeNetPnL ? getTradeNetPnL(t, acc) : { netPnl: parseFloat(t.pnl) || 0 };
      return Number.isFinite(r?.netPnl) ? r.netPnl : 0;
    };
    let netPnl = 0; let wins = 0;
    const setupAgg = new Map();
    for (const t of recentTrades) {
      const n = netPnlFor(t);
      netPnl += n;
      if (n > 0) wins++;
      if (t.setup) {
        const acc = setupAgg.get(t.setup) || { name: t.setup, count: 0, net: 0 };
        acc.count++; acc.net += n;
        setupAgg.set(t.setup, acc);
      }
    }
    const setupsByNet = [...setupAgg.values()].sort((a, b) => b.net - a.net);
    recent = {
      tradeCount: recentTrades.length,
      netPnl,
      winRate: recentTrades.length > 0 ? wins / recentTrades.length : 0,
      strongestSetup: setupsByNet[0] || null,
      weakestSetup: setupsByNet.length > 1 ? setupsByNet[setupsByNet.length - 1] : null,
    };
  }

  // Today's events — keep just title + time for the LLM
  const events = Array.isArray(todayEvents) ? todayEvents.slice(0, 6).map(e => ({
    title: e?.title || e?.name || 'event',
    time: e?.time || e?.startTime || '',
    impact: e?.impact || e?.priority || '',
  })) : [];

  return {
    dateStr,
    topEdges,
    topLeaks,
    recent,
    todayEvents: events,
    priorAdherence: priorAdherence || null,
  };
};

const PLAN_SYSTEM_PROMPT = `You are a trading coach writing a SHORT pre-market plan paragraph for a futures trader for today's session.

ABSOLUTE RULES:
- Every numeric claim or pattern reference MUST come from the context provided. Do not invent stats.
- This is a FORWARD-LOOKING plan, not a recap. Write in the imperative or first-person plural ("Focus on X. Avoid Y. Hands off until 10 because of CPI."). Don't review past performance directly — pull on it implicitly when justifying a focus.
- Do not use bullet points, headings, or markdown. Conversational coaching prose.
- Length: 3-5 sentences. Tight, actionable, scannable in 10 seconds.
- Lead with the strongest edge to lean into TODAY. End with one specific risk to avoid (a recurring leak, a news event, or both).
- If today's news events include a high-impact print, name it and recommend a hands-off window around it (typically 30 min before to 15 min after).

VOICE:
- Direct, prepared, calm. Like a head coach setting the game plan in the locker room before tip-off.
- Specific over general. "Focus on 21-EMA reversals at the open" beats "trade your edges."`;

const buildPlanUserPrompt = (planCtx) => {
  const lines = [];
  lines.push(`Write today's pre-market plan for ${planCtx.dateStr || 'the session ahead'}.`);

  if (planCtx.topEdges.length > 0) {
    lines.push('\nCURRENT EDGES (lean into these):');
    planCtx.topEdges.forEach((e, i) => {
      lines.push(`  ${i + 1}. ${e.title}${e.scopeId ? ` [${e.scope}: ${e.scopeId}]` : ''}`);
      if (e.detail) lines.push(`     ${e.detail}`);
    });
  }

  if (planCtx.topLeaks.length > 0) {
    lines.push('\nCURRENT LEAKS (avoid or gate around):');
    planCtx.topLeaks.forEach((l, i) => {
      lines.push(`  ${i + 1}. ${l.title}${l.scopeId ? ` [${l.scope}: ${l.scopeId}]` : ''}`);
      if (l.detail) lines.push(`     ${l.detail}`);
      if (l.occurrences > 1) lines.push(`     PERSISTENCE: firing ${l.occurrences} runs in a row.`);
    });
  }

  if (planCtx.recent) {
    const sign = planCtx.recent.netPnl >= 0 ? '+' : '−';
    lines.push(`\nRECENT REGIME (last ${planCtx.recent.tradeCount} trades): ${sign}$${Math.abs(Math.round(planCtx.recent.netPnl))}, ${Math.round(planCtx.recent.winRate * 100)}% WR.`);
    if (planCtx.recent.strongestSetup) {
      lines.push(`  Strongest: ${planCtx.recent.strongestSetup.name} (${planCtx.recent.strongestSetup.net >= 0 ? '+' : '−'}$${Math.abs(Math.round(planCtx.recent.strongestSetup.net))} on ${planCtx.recent.strongestSetup.count} trades).`);
    }
    if (planCtx.recent.weakestSetup && planCtx.recent.weakestSetup.net < 0) {
      lines.push(`  Weakest: ${planCtx.recent.weakestSetup.name} (−$${Math.abs(Math.round(planCtx.recent.weakestSetup.net))} on ${planCtx.recent.weakestSetup.count} trades).`);
    }
  }

  if (planCtx.todayEvents.length > 0) {
    lines.push('\nTODAY\'S NEWS EVENTS:');
    planCtx.todayEvents.forEach(e => {
      lines.push(`  - ${e.time ? e.time + ' ET — ' : ''}${e.title}${e.impact ? ` (${e.impact})` : ''}`);
    });
  }

  if (planCtx.priorAdherence) {
    lines.push(`\nYESTERDAY'S PLAN ADHERENCE: ${planCtx.priorAdherence.headline || `${planCtx.priorAdherence.onPlanCount || 0} on-plan, ${planCtx.priorAdherence.offPlanCount || 0} off-plan`}.`);
  }

  lines.push('\nWrite the plan now. 3-5 sentences. Forward-looking, imperative voice, ground every recommendation in the context above.');
  return lines.join('\n');
};

/**
 * Public API: generate today's plan via the LLM narrator.
 *
 * @param {Object} args
 * @param {Object} args.planContext   from buildPlanContext()
 * @param {string} args.apiKey
 * @param {string} [args.model]
 * @param {string} [args.voice]       personality (used for tone consistency, not the system prompt)
 * @param {AbortSignal} [args.signal]
 * @returns {Promise<{ok, text, fromCache, ...}>}  same shape as narrateService
 */
export const generatePlan = async ({
  planContext,
  apiKey,
  model,
  voice = 'mentor',
  signal,
} = {}) => {
  if (!planContext) return { ok: false, error: 'No plan context provided' };
  // We construct the prompt by hand and pass it as observations:[] to
  // narrateService — but narrateService expects observations + scope to
  // build its own user prompt. To bypass that, we pass a single
  // synthetic "observation" containing the full prompt text in its
  // detail field. This is a pragmatic shim; a future refactor could
  // expose a generic narrate() that takes raw user prompts directly.
  // Cleaner alternative for v1: call the API client directly here. But
  // that bypasses the IPC-secured key path. Trade-off: shim is fine.
  const userPrompt = buildPlanUserPrompt(planContext);
  // Synthetic observation that the prompt builder will format with the
  // user prompt. We override the system prompt by passing it via a
  // direct API call — the existing narrateService only takes voice, not
  // a system override. So we route through the underlying API client
  // here for the system-prompt swap. Reuses the IPC route automatically.
  const { callAnthropic, DEFAULT_MODEL, DEFAULT_MAX_TOKENS } = await import('../llm/anthropicClient.js');
  return callAnthropic({
    apiKey,
    system: PLAN_SYSTEM_PROMPT,
    user: userPrompt,
    model: model || DEFAULT_MODEL,
    maxTokens: DEFAULT_MAX_TOKENS,
    signal,
  });
};

// Test-friendly exports
export {
  PLAN_SYSTEM_PROMPT,
  buildPlanUserPrompt,
};
